import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  ArrowUp, ArrowDown, Save, Loader2, Star, Pencil, Trash2, Plus, Check, X,
  ChevronDown, ChevronRight, Video as VideoIcon, EyeOff, Eye, Archive, ArchiveRestore, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { clipLabel } from '../lib/media';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Built-in rail keys — auto-populated on the landing page. Custom rails
// (key doesn't match one of these) get their contents from an explicit
// `items` list the admin curates below.
const BUILTIN_KEYS = new Set([
  'just-added', 'new-library', 'coaches', 'disciplines', 'upcoming', 'past',
]);

const genCustomKey = () =>
  `custom-${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-4)}`;

const KIND_LABEL = {
  library: '📺 Library',
  'event-video': '🎬 Event Video',
  'event-photo': '📷 Event Photo',
  teaser: '📱 Teaser (9:16)',
  'live-teaser': '🔴 Live Teaser',
  'studio-project': '🎞 Video Studio',
  upcoming: '📅 Upcoming Event',
  past: '⏮ Past Event',
  coach: '🏇 Coach',
};

// Kept in sync with DISCIPLINE_ORDER in NetflixLandingPage.jsx — these are the
// sub-rails the landing page renders when the "By Discipline" rail is enabled.
const DISCIPLINE_ORDER = [
  { key: 'heading', label: 'Head & Heel · Heading' },
  { key: 'heeling', label: 'Head & Heel · Heeling' },
  { key: 'breakaway', label: 'Breakaway' },
  { key: 'tie-down', label: 'Tie-Down' },
  { key: 'barrel-racing', label: 'Barrel Racing' },
  { key: 'ranch-roping', label: 'Ranch Roping' },
  { key: 'general', label: 'Ranch Life' },
];

const HomePageRailsTab = () => {
  const [rails, setRails] = useState([]);
  const [library, setLibrary] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [eventVideos, setEventVideos] = useState([]);
  const [eventPhotos, setEventPhotos] = useState([]);
  const [teasers, setTeasers] = useState([]);
  const [liveTeasers, setLiveTeasers] = useState([]);
  const [studioProjects, setStudioProjects] = useState([]);
  const [taxonomies, setTaxonomies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingLabelIdx, setEditingLabelIdx] = useState(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [pickerIdx, setPickerIdx] = useState(null); // rail idx for the item picker
  const [disciplinesOpen, setDisciplinesOpen] = useState(false); // expand/collapse By Discipline breakdown
  // Dirty flag — flips true whenever the admin changes anything that isn't
  // yet persisted. Used to warn before navigating/reloading and to style the
  // Save Layout button so it's obvious a save is pending.
  const [dirty, setDirty] = useState(false);

  // Button label changes with the rail's content_type so admins see what the
  // picker will show them.
  const PICK_LABELS = {
    mixed: 'Pick Items',
    videos: 'Pick Videos',
    events: 'Pick Events',
    coaches: 'Pick Coaches',
    photos: 'Pick Photos',
  };

  const setContentType = (idx, ctype) => {
    const copy = rails.slice();
    copy[idx] = { ...copy[idx], content_type: ctype };
    // Prune items that no longer match the new content_type so the list
    // doesn't carry stale picks across type changes.
    if (ctype !== 'mixed' && Array.isArray(copy[idx].items)) {
      const allowedKinds = {
        videos: ['library', 'event-video', 'teaser', 'live-teaser', 'studio-project'],
        events: ['upcoming', 'past'],
        coaches: ['coach'],
        photos: ['event-photo'],
      }[ctype] || [];
      copy[idx].items = copy[idx].items.filter((it) => allowedKinds.includes(it.kind));
    }
    setRails(copy);
    setDirty(true);
  };

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const [cfg, lib, co, up, pa, tax, evVids, evPhotos, tz, ltz, sp] = await Promise.allSettled([
          axios.get(`${API_URL}/api/home-config`),
          // Admin endpoints return every video/event (draft + inactive + all
          // 1000+ rows) so the picker can pin anything, not just what public
          // pages show.
          axios.get(`${API_URL}/api/admin/library`, { headers: authHeaders }),
          axios.get(`${API_URL}/api/coaches`),
          axios.get(`${API_URL}/api/admin/events`, { headers: authHeaders }),
          axios.get(`${API_URL}/api/admin/past-events`, { headers: authHeaders }),
          axios.get(`${API_URL}/api/taxonomies`),
          // Event-attached videos — each belongs to an existing event via
          // event_id. Surfaced in the picker so admins can pin a specific
          // run/round/performance into a custom rail (e.g. "The Jonathan
          // Cole Show" rail with hand-picked clips).
          axios.get(`${API_URL}/api/admin/event-videos`, { headers: authHeaders }),
          // Event photos — gallery shots. Admin can pin individual photos
          // onto rails that link out to /gallery/{event_id} on tap.
          axios.get(`${API_URL}/api/admin/event-photos`, { headers: authHeaders }),
          // Short teaser videos (9:16) + landing-page live-event teasers.
          // Also pickable so an admin can repromote them inside a rail.
          axios.get(`${API_URL}/api/teaser-videos`),
          axios.get(`${API_URL}/api/live-event-teasers`),
          // Video Studio projects — coach-produced edited videos with
          // annotations/voice-over. Admin-only aggregation across coaches.
          axios.get(`${API_URL}/api/admin/video-projects`, { headers: authHeaders }),
        ]);
        if (cfg.status === 'fulfilled') setRails(cfg.value.data?.rails || []);
        if (lib.status === 'fulfilled') setLibrary(lib.value.data?.videos || []);
        if (co.status === 'fulfilled') setCoaches(co.value.data?.coaches || []);
        if (up.status === 'fulfilled') setUpcoming(up.value.data?.events || []);
        if (pa.status === 'fulfilled') setPast(pa.value.data?.events || []);
        if (tax.status === 'fulfilled') setTaxonomies(tax.value.data?.taxonomies || null);
        if (evVids.status === 'fulfilled') setEventVideos(evVids.value.data?.videos || []);
        if (evPhotos.status === 'fulfilled') setEventPhotos(evPhotos.value.data?.photos || []);
        if (tz.status === 'fulfilled') setTeasers(tz.value.data?.videos || []);
        if (ltz.status === 'fulfilled') setLiveTeasers(ltz.value.data?.videos || []);
        if (sp.status === 'fulfilled') setStudioProjects(sp.value.data?.projects || []);
      } catch (e) {
        toast.error('Failed to load home page settings');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, []);

  // Warn the admin if they try to leave with unsaved picker/order changes —
  // the top complaint before this was items vanishing after a reload because
  // the global Save Layout button was never clicked.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Which items in this rail are eligible for the "Featured pin" dropdown.
  const pinnableItemsFor = (rail) => {
    if (!rail) return null;
    if (rail.custom) {
      const resolve = (it) => {
        if (!it) return null;
        if (it.kind === 'library') {
          const v = library.find((x) => x.id === it.id);
          return v ? { id: v.id, label: `📺 ${v.title}` } : null;
        }
        if (it.kind === 'coach') {
          const c = coaches.find((x) => x.id === it.id);
          return c ? { id: c.id, label: `🏇 ${c.name}` } : null;
        }
        if (it.kind === 'upcoming') {
          const e = upcoming.find((x) => x.id === it.id);
          return e ? { id: e.id, label: `📅 ${e.title}` } : null;
        }
        if (it.kind === 'past') {
          const e = past.find((x) => x.id === it.id);
          return e ? { id: e.id, label: `⏮ ${e.title}` } : null;
        }
        return null;
      };
      return (rail.items || []).map(resolve).filter(Boolean);
    }
    switch (rail.key) {
      case 'just-added': {
        // Pool everything so admin can pin any recent-ish item (not just top 5).
        const pool = [
          ...library.map((v) => ({ id: v.id, label: `📺 ${v.title}` })),
          ...upcoming.map((e) => ({ id: e.id, label: `📅 ${e.title}` })),
          ...past.map((e) => ({ id: e.id, label: `⏮ ${e.title}` })),
        ];
        return pool;
      }
      case 'new-library':
        // Every library video is a valid featured pin.
        return library.map((v) => ({ id: v.id, label: v.title }));
      case 'coaches':
        return coaches.map((c) => ({ id: c.id, label: c.name }));
      case 'upcoming':
        return upcoming.map((e) => ({ id: e.id, label: e.title }));
      case 'past':
        return past.map((e) => ({ id: e.id, label: e.title }));
      case 'disciplines':
      default:
        return null;
    }
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rails.length) return;
    const copy = rails.slice();
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    setRails(copy);
    setDirty(true);
  };

  const toggle = (idx) => {
    const copy = rails.slice();
    copy[idx] = { ...copy[idx], enabled: !copy[idx].enabled };
    setRails(copy);
    setDirty(true);
  };

  const setFeatured = (idx, id) => {
    const copy = rails.slice();
    copy[idx] = { ...copy[idx], featured_id: id || null };
    setRails(copy);
    setDirty(true);
  };

  // Bucket library videos by their `category` field so the "By Discipline"
  // expander can show admin exactly which videos populate each sub-rail.
  const libraryByDiscipline = useMemo(() => {
    const map = {};
    for (const v of library) {
      const c = (v.category || 'general').toLowerCase();
      if (!map[c]) map[c] = [];
      map[c].push(v);
    }
    return map;
  }, [library]);

  // Toggle a single discipline sub-rail visible/hidden on the landing page.
  const toggleDisciplineHidden = (railIdx, disciplineKey) => {
    const copy = rails.slice();
    const hidden = new Set(copy[railIdx].disciplines_hidden || []);
    if (hidden.has(disciplineKey)) hidden.delete(disciplineKey);
    else hidden.add(disciplineKey);
    copy[railIdx] = { ...copy[railIdx], disciplines_hidden: Array.from(hidden) };
    setRails(copy);
    setDirty(true);
  };

  // Hide/show one specific library video from the discipline rails on `/`.
  // Persists in `disciplines_hidden_videos` on the rail config; the video
  // itself is not modified and still shows in the library page.
  const toggleVideoHidden = (railIdx, videoId) => {
    const copy = rails.slice();
    const hidden = new Set(copy[railIdx].disciplines_hidden_videos || []);
    if (hidden.has(videoId)) hidden.delete(videoId);
    else hidden.add(videoId);
    copy[railIdx] = { ...copy[railIdx], disciplines_hidden_videos: Array.from(hidden) };
    setRails(copy);
    setDirty(true);
  };

  // Re-categorize a library video (fixes wrong tagging). Persists immediately
  // via the existing admin library endpoint and reflects in local state.
  const moveVideoToDiscipline = async (videoId, newCategory) => {
    try {
      await axios.put(`${API_URL}/api/admin/library/${videoId}`, { category: newCategory }, { headers: authHeaders });
      setLibrary((prev) => prev.map((v) => v.id === videoId ? { ...v, category: newCategory } : v));
      toast.success(`Moved to ${newCategory}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Move failed');
    }
  };

  // Rename a discipline sub-rail label without touching video data — writes
  // into rail.discipline_labels: { key: label }. Auto-generated title is used
  // if no custom label exists.
  const setDisciplineLabel = (railIdx, disciplineKey, newLabel) => {
    const copy = rails.slice();
    const labels = { ...(copy[railIdx].discipline_labels || {}) };
    const clean = String(newLabel || '').trim();
    if (!clean) delete labels[disciplineKey];
    else labels[disciplineKey] = clean;
    copy[railIdx] = { ...copy[railIdx], discipline_labels: labels };
    setRails(copy);
    setDirty(true);
  };

  // Assemble the full discipline list dynamically: seven canonical rails
  // (heading, heeling, …) first, then any custom category from the taxonomy
  // or from library data. Ensures brand-new categories show up automatically.
  const titleize = (k) => String(k || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const dynamicDisciplineList = useMemo(() => {
    const canonicalSet = new Set(DISCIPLINE_ORDER.map((d) => d.key));
    const taxKeys = (taxonomies?.video_category || []).map((x) => String(x).toLowerCase());
    const libKeys = Object.keys(libraryByDiscipline || {});
    const extras = [...new Set([...taxKeys, ...libKeys])]
      .filter((k) => !canonicalSet.has(k))
      .sort();
    return [
      ...DISCIPLINE_ORDER,
      ...extras.map((k) => ({ key: k, label: titleize(k), custom: true })),
    ];
    // eslint-disable-next-line
  }, [taxonomies, libraryByDiscipline]);

  const startEditLabel = (idx) => {
    setEditingLabelIdx(idx);
    setLabelDraft(rails[idx].label);
  };

  const saveEditLabel = () => {
    if (editingLabelIdx === null) return;
    const copy = rails.slice();
    copy[editingLabelIdx] = { ...copy[editingLabelIdx], label: labelDraft.trim() || copy[editingLabelIdx].key };
    setRails(copy);
    setDirty(true);
    setEditingLabelIdx(null);
    setLabelDraft('');
  };

  const deleteRail = (idx) => {
    const rail = rails[idx];
    if (!rail?.custom) return;
    if (!window.confirm(`Delete the "${rail.label}" rail? This only removes the rail — the underlying videos/events stay put.`)) return;
    setRails(rails.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const toggleArchived = (idx) => {
    const copy = rails.slice();
    copy[idx] = { ...copy[idx], archived: !copy[idx].archived };
    setRails(copy);
    setDirty(true);
  };

  // Hand-picked "Elevated" rail with gold styling, inserted at the top.
  const createElevatedRail = () => {
    setRails([
      { key: genCustomKey(), label: 'Elevated', enabled: true, featured_id: null, custom: true, content_type: 'videos', style: 'elevated', items: [] },
      ...rails,
    ]);
    setDirty(true);
    setTimeout(() => { setEditingLabelIdx(0); setLabelDraft('Elevated'); }, 0);
  };

  const [archivedOpen, setArchivedOpen] = useState(false);
  const archivedCount = rails.filter((r) => r.archived).length;

  const createCustomRail = () => {
    const key = genCustomKey();
    setRails([
      ...rails,
      {
        key,
        label: 'New Rail',
        enabled: true,
        featured_id: null,
        custom: true,
        items: [],
      },
    ]);
    setDirty(true);
    // Immediately open the label editor for the new rail so admin can name it.
    setTimeout(() => {
      setEditingLabelIdx(rails.length);
      setLabelDraft('New Rail');
    }, 0);
  };

  const save = async (railsOverride = null) => {
    const payload = railsOverride || rails;
    setSaving(true);
    try {
      const resp = await axios.put(
        `${API_URL}/api/admin/home-config`,
        { rails: payload },
        { headers: authHeaders },
      );
      // Trust the server echo so the admin sees exactly what persisted (any
      // dropped invalid items become obvious here).
      if (resp?.data?.rails) setRails(resp.data.rails);
      setDirty(false);
      toast.success('Home page layout saved');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Item picker for a custom rail — combined multi-select across content types.
  const activePicker = pickerIdx !== null ? rails[pickerIdx] : null;
  const [pickerSearch, setPickerSearch] = useState('');
  // Discipline filter inside the picker so a rail named "Barrel Racing
  // Events" doesn't force the admin to scroll a thousand mixed items. Scans
  // every text field on each record (title, description, tags, category,
  // event_type, filename, parent event title/tags) for the discipline key.
  const [pickerDiscipline, setPickerDiscipline] = useState('all');

  const allPickableItems = useMemo(() => {
    // Pre-compute a lowercased haystack per item from EVERY text field we
    // have on the underlying record. Keeps the discipline filter O(n) and
    // tolerates loose tagging ("barrel racing" in a title, "barrel-racing"
    // in a tag, "Barrel" in event_type — all match the same discipline).
    const hay = (parts) => parts.filter(Boolean).map((p) => String(p).toLowerCase()).join(' | ');
    const eventVideoHay = (v) => {
      const parent = upcoming.find((e) => e.id === v.event_id) || past.find((e) => e.id === v.event_id);
      return hay([
        v.title, v.filename, v.event_title,
        ...(v.tags || []),
        parent?.title, parent?.description, parent?.event_type, parent?.location,
        ...((parent?.tags) || []),
      ]);
    };
    return [
      ...library.map((v) => ({
        kind: 'library', id: v.id, title: v.title,
        sub: [v.category, v.difficulty].filter(Boolean).join(' · '),
        isDraft: v.status && v.status !== 'active',
        _hay: hay([v.title, v.description, v.category, v.difficulty, ...(v.tags || [])]),
      })),
      ...eventVideos.map((v) => ({
        kind: 'event-video', id: v.id, title: clipLabel(v),
        sub: v.event_title ? `from ${v.event_title}` : 'from event',
        isDraft: false,
        _hay: eventVideoHay(v),
      })),
      ...eventPhotos.map((p) => {
        const parent = upcoming.find((e) => e.id === p.event_id) || past.find((e) => e.id === p.event_id);
        return {
          kind: 'event-photo', id: p.id, title: p.filename || 'Untitled photo',
          sub: p.event_title ? `from ${p.event_title}` : 'from event',
          isDraft: false,
          _hay: hay([
            p.filename, p.event_title,
            parent?.title, parent?.description, parent?.event_type, parent?.location,
          ]),
        };
      }),
      // Teaser videos (9:16) and live-event teasers live in a settings
      // document as `[{url, title}]`. There's no stored id, so we key them
      // by url — if the admin replaces the url the pick is invalid and
      // gets filtered out, which is the right behavior.
      ...teasers.filter((t) => (t?.url || '').trim()).map((t) => ({
        kind: 'teaser', id: t.url, title: t.title || 'Teaser',
        sub: 'Vertical 9:16',
        isDraft: false,
        _hay: hay([t.title, 'teaser']),
      })),
      ...liveTeasers.filter((t) => (t?.url || '').trim()).map((t) => ({
        kind: 'live-teaser', id: t.url, title: t.title || 'Live teaser',
        sub: 'Live event teaser',
        isDraft: false,
        _hay: hay([t.title, 'live teaser']),
      })),
      // Video Studio projects — coach-produced edited videos. Only include
      // ones with a renderable `final_video_url` (status !== 'draft'); the
      // picker still shows drafts with a badge so admin knows to publish.
      ...studioProjects.map((p) => ({
        kind: 'studio-project', id: p.id, title: p.title || 'Studio project',
        sub: p.coach_name ? `by ${p.coach_name}` : 'Video Studio',
        isDraft: !p.final_video_url || p.status === 'draft',
        _hay: hay([p.title, p.coach_name, p.status]),
      })),
      ...upcoming.map((e) => ({
        kind: 'upcoming', id: e.id, title: e.title,
        sub: e.event_date || 'TBA',
        isDraft: e.is_published === false,
        _hay: hay([e.title, e.description, e.event_type, e.location, e.association, ...(e.tags || [])]),
      })),
      ...past.map((e) => ({
        kind: 'past', id: e.id, title: e.title,
        sub: e.event_date || 'Past event',
        isDraft: e.status && e.status !== 'active',
        _hay: hay([e.title, e.description, e.event_type, e.location, ...(e.tags || [])]),
      })),
      ...coaches.map((c) => ({
        kind: 'coach', id: c.id, title: c.name, sub: c.region || '', isDraft: false,
        _hay: hay([c.name, c.bio, c.tagline, c.region, ...(c.disciplines || [])]),
      })),
    ];
  }, [library, eventVideos, eventPhotos, teasers, liveTeasers, studioProjects, upcoming, past, coaches]);

  const filteredPickable = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const typeFilter = activePicker?.content_type || 'mixed';
    const base = typeFilter === 'mixed'
      ? allPickableItems
      : typeFilter === 'videos'
        ? allPickableItems.filter((x) => ['library', 'event-video', 'teaser', 'live-teaser', 'studio-project'].includes(x.kind))
        : typeFilter === 'events'
          ? allPickableItems.filter((x) => x.kind === 'upcoming' || x.kind === 'past')
          : typeFilter === 'photos'
            ? allPickableItems.filter((x) => x.kind === 'event-photo')
            : /* coaches */ allPickableItems.filter((x) => x.kind === 'coach');
    // Discipline filter — match the raw key (barrel-racing), the human form
    // (barrel racing) and a loose root (barrel) so slightly off tags still
    // get caught. Always keep already-picked items visible even if they
    // don't match, so admin can un-check them from the same filtered view.
    let afterDiscipline = base;
    if (pickerDiscipline && pickerDiscipline !== 'all') {
      const key = pickerDiscipline.toLowerCase();
      const human = key.replace(/-/g, ' ');
      const root = human.split(' ')[0];
      const picked = new Set((activePicker?.items || []).map((it) => `${it.kind}:${it.id}`));
      afterDiscipline = base.filter((x) => {
        if (picked.has(`${x.kind}:${x.id}`)) return true;
        const h = x._hay || '';
        return h.includes(key) || h.includes(human) || (root.length >= 4 && h.includes(root));
      });
    }
    // Every word must appear somewhere in the title, subtitle or any text field.
    const words = q.split(/\s+/).filter(Boolean);
    const afterSearch = words.length === 0
      ? afterDiscipline
      : afterDiscipline.filter((x) => {
          const h = `${x.title || ''} | ${x.sub || ''} | ${x._hay || ''}`.toLowerCase();
          return words.every((w) => h.includes(w));
        });
    // Float checked items to the top so bulk-select across pages is obvious.
    const picked = new Set((activePicker?.items || []).map((it) => `${it.kind}:${it.id}`));
    return afterSearch.slice().sort((a, b) => {
      const ap = picked.has(`${a.kind}:${a.id}`) ? 0 : 1;
      const bp = picked.has(`${b.kind}:${b.id}`) ? 0 : 1;
      return ap - bp;
    });
  }, [pickerSearch, pickerDiscipline, allPickableItems, activePicker]);

  const toggleItemInRail = (kind, id) => {
    if (pickerIdx === null) return;
    const copy = rails.slice();
    const items = (copy[pickerIdx].items || []).slice();
    const foundIdx = items.findIndex((it) => it.kind === kind && it.id === id);
    if (foundIdx >= 0) items.splice(foundIdx, 1);
    else items.push({ kind, id });
    copy[pickerIdx] = { ...copy[pickerIdx], items };
    setRails(copy);
    setDirty(true);
  };

  // Bulk add every visible (filtered) item to the active rail — one click
  // after narrowing by discipline, no need to tick 20 checkboxes.
  const checkAllVisible = () => {
    if (pickerIdx === null) return;
    const copy = rails.slice();
    const current = (copy[pickerIdx].items || []).slice();
    const have = new Set(current.map((it) => `${it.kind}:${it.id}`));
    let added = 0;
    for (const x of filteredPickable) {
      const key = `${x.kind}:${x.id}`;
      if (!have.has(key)) {
        current.push({ kind: x.kind, id: x.id });
        have.add(key);
        added += 1;
      }
    }
    if (added === 0) return;
    copy[pickerIdx] = { ...copy[pickerIdx], items: current };
    setRails(copy);
    setDirty(true);
    toast.success(`Added ${added} item${added === 1 ? '' : 's'}`);
  };

  // Remove every currently-visible item from the rail. Pairs with
  // checkAllVisible for quick undo if the admin bulk-adds the wrong set.
  const uncheckAllVisible = () => {
    if (pickerIdx === null) return;
    const copy = rails.slice();
    const current = (copy[pickerIdx].items || []).slice();
    const visible = new Set(filteredPickable.map((x) => `${x.kind}:${x.id}`));
    const next = current.filter((it) => !visible.has(`${it.kind}:${it.id}`));
    const removed = current.length - next.length;
    if (removed === 0) return;
    copy[pickerIdx] = { ...copy[pickerIdx], items: next };
    setRails(copy);
    setDirty(true);
    toast.success(`Removed ${removed} item${removed === 1 ? '' : 's'}`);
  };

  const isItemInActive = (kind, id) => {
    if (!activePicker) return false;
    return (activePicker.items || []).some((it) => it.kind === kind && it.id === id);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground p-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="home-page-rails-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Use ↑↓ to reorder rails, rename with the pencil, and toggle a rail off to hide it. Pin one item as <strong>Featured</strong> and it will jump to the front of that rail. Create custom rails and hand-pick items for total control.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={createElevatedRail} data-testid="home-rails-create-elevated">
            <Sparkles className="w-4 h-4 mr-2 text-amber-500" /> Elevated Rail
          </Button>
          <Button variant="outline" onClick={createCustomRail} data-testid="home-rails-create">
            <Plus className="w-4 h-4 mr-2" /> New Rail
          </Button>
          <Button
            onClick={() => save()}
            disabled={saving}
            data-testid="home-rails-save"
            className={dirty ? 'ring-2 ring-amber-500 ring-offset-2 animate-pulse' : ''}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {dirty ? 'Save Layout *' : 'Save Layout'}
          </Button>
        </div>
      </div>

      {dirty && (
        <div
          className="rounded-md border border-amber-400 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-center gap-2"
          data-testid="home-rails-unsaved-banner"
        >
          <span className="font-medium">Unsaved changes.</span>
          <span className="opacity-80">Click "Save Layout" to persist — otherwise reloading will lose your picks.</span>
        </div>
      )}

      <FeaturedFreeVideoCard />

      <div className="space-y-2">
        {rails.map((r, idx) => {
          if (r.archived && !archivedOpen) return null;
          const items = pinnableItemsFor(r);
          const isBuiltin = BUILTIN_KEYS.has(r.key);
          const isEditing = editingLabelIdx === idx;
          return (
            <Card key={r.key} data-testid={`home-rail-row-${r.key}`} className={r.archived ? 'opacity-60 border-dashed' : (r.style === 'elevated' ? 'border-amber-400' : '')}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      data-testid={`home-rail-up-${r.key}`}
                      aria-label={`Move ${r.label} up`}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => move(idx, 1)}
                      disabled={idx === rails.length - 1}
                      data-testid={`home-rail-down-${r.key}`}
                      aria-label={`Move ${r.label} down`}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={labelDraft}
                          onChange={(e) => setLabelDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEditLabel(); if (e.key === 'Escape') setEditingLabelIdx(null); }}
                          autoFocus
                          className="h-8 max-w-[280px]"
                          data-testid={`home-rail-label-input-${r.key}`}
                        />
                        <Button size="icon" variant="ghost" onClick={saveEditLabel} data-testid={`home-rail-label-save-${r.key}`}>
                          <Check className="w-4 h-4 text-green-600" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setEditingLabelIdx(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">
                            {idx + 1}. {r.label}
                            {r.custom && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">Custom</span>}
                            {r.style === 'elevated' && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-500 text-white" data-testid={`home-rail-elevated-badge-${r.key}`}>Elevated</span>}
                            {r.archived && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border" data-testid={`home-rail-archived-badge-${r.key}`}>Archived</span>}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            key: <code>{r.key}</code>
                            {r.custom && (
                              <span className="ml-2">
                                · {(r.items || []).length} item{(r.items || []).length === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => startEditLabel(idx)}
                          data-testid={`home-rail-edit-${r.key}`}
                          aria-label={`Rename ${r.label}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {r.custom && (
                      <>
                        <select
                          className="text-xs border rounded-md px-2 py-1.5 bg-background"
                          value={r.content_type || 'mixed'}
                          onChange={(e) => setContentType(idx, e.target.value)}
                          data-testid={`home-rail-content-type-${r.key}`}
                          title="What this rail can contain"
                        >
                          <option value="mixed">Type: Mixed</option>
                          <option value="videos">Type: Videos</option>
                          <option value="events">Type: Events</option>
                          <option value="photos">Type: Photos</option>
                          <option value="coaches">Type: Coaches</option>
                        </select>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPickerIdx(idx);
                            setPickerSearch('');
                            // Discipline auto-detect only kicks in for empty
                            // rails — if the admin has already picked items,
                            // we open with "All disciplines" so their picks
                            // are visible instead of silently filtered out
                            // by a label-derived keyword that doesn't match
                            // every record they picked.
                            const alreadyPicked = (r.items || []).length > 0;
                            if (alreadyPicked) {
                              setPickerDiscipline('all');
                            } else {
                              const label = (r.label || '').toLowerCase();
                              const guess = dynamicDisciplineList.find((d) => {
                                const k = d.key.replace(/-/g, ' ');
                                const root = k.split(' ')[0];
                                return label.includes(k) || label.includes(d.key) || (root.length >= 4 && label.includes(root));
                              });
                              setPickerDiscipline(guess ? guess.key : 'all');
                            }
                          }}
                          data-testid={`home-rail-pick-${r.key}`}
                        >
                          {PICK_LABELS[r.content_type || 'mixed']} ({(r.items || []).length})
                        </Button>
                      </>
                    )}

                    {items && items.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-amber-500" aria-hidden="true" />
                        <select
                          className="text-sm border rounded-md px-2 py-1.5 bg-background max-w-[220px]"
                          value={r.featured_id || ''}
                          onChange={(e) => setFeatured(idx, e.target.value)}
                          data-testid={`home-rail-featured-${r.key}`}
                        >
                          <option value="">— No featured pin —</option>
                          {items.map((it) => (
                            <option key={it.id} value={it.id}>
                              {it.label.length > 50 ? `${it.label.slice(0, 50)}…` : it.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {r.enabled ? 'Visible' : 'Hidden'}
                      </span>
                      <Switch
                        checked={!!r.enabled}
                        onCheckedChange={() => toggle(idx)}
                        data-testid={`home-rail-toggle-${r.key}`}
                      />
                    </div>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleArchived(idx)}
                      data-testid={`home-rail-archive-${r.key}`}
                      title={r.archived ? 'Restore this rail' : 'Archive — hides it from the home page and tucks it away here, picks are kept'}
                    >
                      {r.archived
                        ? <><ArchiveRestore className="w-4 h-4 mr-1" /> Restore</>
                        : <><Archive className="w-4 h-4 mr-1" /> Archive</>}
                    </Button>

                    {!isBuiltin && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => deleteRail(idx)}
                        data-testid={`home-rail-delete-${r.key}`}
                        aria-label={`Delete ${r.label}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* By Discipline expander — shows which library videos populate
                    each sub-rail and lets admin hide individual disciplines. */}
                {r.key === 'disciplines' && (
                  <div className="mt-3 border-t pt-3">
                    <button
                      type="button"
                      onClick={() => setDisciplinesOpen(!disciplinesOpen)}
                      className="flex items-center gap-2 text-sm font-medium hover:opacity-80"
                      data-testid="home-rail-disciplines-toggle"
                    >
                      {disciplinesOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      What's inside? ({dynamicDisciplineList.filter((d) => (libraryByDiscipline[d.key] || []).length > 0).length} disciplines · {library.length} total videos)
                    </button>

                    {disciplinesOpen && (
                      <div className="mt-3 space-y-2" data-testid="home-rail-disciplines-panel">
                        <p className="text-xs text-muted-foreground">
                          Each sub-rail below is auto-built from library videos where <code>category</code> matches the discipline. Toggle a discipline off to hide its rail on the home page. Edit a video's category from <strong>Admin → Library</strong>.
                        </p>
                        {dynamicDisciplineList.map((d) => {
                          const vids = libraryByDiscipline[d.key] || [];
                          const isHidden = (r.disciplines_hidden || []).includes(d.key);
                          const customLabel = (r.discipline_labels || {})[d.key] || '';
                          const displayLabel = customLabel || d.label;
                          return (
                            <div
                              key={d.key}
                              className={`rounded-md border p-3 ${isHidden ? 'opacity-60 bg-muted/30' : 'bg-background'}`}
                              data-testid={`home-rail-discipline-${d.key}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-semibold truncate">
                                    {displayLabel}
                                    {d.custom && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">Auto-detected</span>}
                                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                                      · {vids.length} video{vids.length === 1 ? '' : 's'}
                                    </span>
                                    <span className="ml-2 text-[10px] font-mono uppercase text-muted-foreground">
                                      category: {d.key}
                                    </span>
                                  </div>
                                  {/* Inline rename — leave blank to fall back to the auto-title. */}
                                  <Input
                                    placeholder={`Rename display label (default: ${d.label})`}
                                    value={customLabel}
                                    onChange={(e) => setDisciplineLabel(idx, d.key, e.target.value)}
                                    className="h-7 mt-1 text-xs max-w-[360px]"
                                    data-testid={`home-rail-discipline-label-${d.key}`}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => toggleDisciplineHidden(idx, d.key)}
                                  data-testid={`home-rail-discipline-toggle-${d.key}`}
                                >
                                  {isHidden ? (
                                    <><Eye className="w-3.5 h-3.5 mr-1.5" /> Show</>
                                  ) : (
                                    <><EyeOff className="w-3.5 h-3.5 mr-1.5" /> Hide</>
                                  )}
                                </Button>
                              </div>
                              {vids.length === 0 ? (
                                <div className="mt-2 text-xs text-muted-foreground italic">
                                  No videos in this category yet — tag videos with <code>{d.key}</code> in Admin → Library.
                                </div>
                              ) : (
                                <ul className="mt-2 space-y-1">
                                  {vids.slice(0, 12).map((v) => {
                                    const vidHidden = ((r.disciplines_hidden_videos) || []).includes(v.id);
                                    return (
                                      <li
                                        key={v.id}
                                        className={`flex items-center gap-2 text-xs ${vidHidden ? 'opacity-50' : 'text-muted-foreground'}`}
                                        data-testid={`home-rail-discipline-video-${v.id}`}
                                      >
                                        <VideoIcon className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate flex-1">{v.title}</span>
                                        {v.difficulty && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{v.difficulty}</span>
                                        )}
                                        {/* Move to another discipline (fixes wrong tagging). */}
                                        <select
                                          className="text-[11px] border rounded px-1 py-0.5 bg-background max-w-[130px]"
                                          value=""
                                          onChange={(e) => {
                                            if (!e.target.value) return;
                                            const target = e.target.value;
                                            e.target.value = '';
                                            moveVideoToDiscipline(v.id, target);
                                          }}
                                          data-testid={`home-rail-discipline-move-${v.id}`}
                                          title="Move to another discipline"
                                        >
                                          <option value="">Move to…</option>
                                          {dynamicDisciplineList.filter((x) => x.key !== d.key).map((x) => (
                                            <option key={x.key} value={x.key}>{(r.discipline_labels || {})[x.key] || x.label}</option>
                                          ))}
                                        </select>
                                        {/* Hide this specific clip from the home page. */}
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 px-2 text-[11px]"
                                          onClick={() => toggleVideoHidden(idx, v.id)}
                                          data-testid={`home-rail-discipline-video-toggle-${v.id}`}
                                        >
                                          {vidHidden ? (
                                            <><Eye className="w-3 h-3 mr-1" /> Show</>
                                          ) : (
                                            <><EyeOff className="w-3 h-3 mr-1" /> Hide</>
                                          )}
                                        </Button>
                                      </li>
                                    );
                                  })}
                                  {vids.length > 12 && (
                                    <li className="text-xs text-muted-foreground italic pl-5">
                                      + {vids.length - 12} more…
                                    </li>
                                  )}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {archivedCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setArchivedOpen((v) => !v)}
          data-testid="home-rails-archived-toggle"
        >
          {archivedOpen ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
          {archivedOpen ? 'Hide' : 'Show'} archived rails ({archivedCount})
        </Button>
      )}

      <p className="text-xs text-muted-foreground">
        Note: the "🔴 Live Now" rail appears automatically at the top whenever a broadcast is on-air and is not configurable here. Built-in rails can be renamed, reordered and hidden but not deleted; custom rails support all four (rename, reorder, hide, delete) and let you hand-pick items.
      </p>

      {/* Item picker dialog */}
      <Dialog
        open={pickerIdx !== null}
        onOpenChange={async (o) => {
          if (!o) {
            // Auto-save on close so admins can't lose their picks by hitting
            // Esc or clicking the backdrop. Only fire if we have unsaved
            // changes to avoid a redundant PUT on every open/close.
            if (dirty) await save();
            setPickerIdx(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Pick items for "{activePicker?.label}"</DialogTitle>
            <DialogDescription>
              Choose any mix of library videos, upcoming events, past events and coaches. Items appear on the home page in the order you check them. Changes auto-save when you click <strong>Done</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 pt-3 pb-2 flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Search titles…"
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              data-testid="home-rail-picker-search"
              className="flex-1"
            />
            <select
              value={pickerDiscipline}
              onChange={(e) => setPickerDiscipline(e.target.value)}
              data-testid="home-rail-picker-discipline"
              className="h-10 rounded-md border border-input bg-background px-3 text-sm sm:w-56"
              title="Narrow the list to one discipline — matches title, description, tags, event type, and category across every record."
            >
              <option value="all">All disciplines</option>
              {dynamicDisciplineList.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </div>
          <div className="px-6 pb-2 flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-muted-foreground">
              Showing <strong>{filteredPickable.length}</strong> of {allPickableItems.length} items
              {pickerDiscipline !== 'all' && ` · filtered by ${dynamicDisciplineList.find((d) => d.key === pickerDiscipline)?.label || pickerDiscipline}`}
              {' · '}<strong>{(activePicker?.items || []).length}</strong> checked (shown first)
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={checkAllVisible}
                data-testid="home-rail-picker-check-all"
                disabled={filteredPickable.length === 0}
                title="Add every item currently visible in this list to the rail"
              >
                <Check className="w-3.5 h-3.5 mr-1" /> Check all shown
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={uncheckAllVisible}
                data-testid="home-rail-picker-uncheck-all"
                disabled={filteredPickable.length === 0}
              >
                <X className="w-3.5 h-3.5 mr-1" /> Uncheck shown
              </Button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto border-y divide-y">
            {filteredPickable.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center space-y-3">
                <div>
                  No items match{pickerDiscipline !== 'all' && (
                    <> the <strong>{dynamicDisciplineList.find((d) => d.key === pickerDiscipline)?.label || pickerDiscipline}</strong> filter</>
                  )}{pickerSearch && <> matching "<strong>{pickerSearch}</strong>"</>}.
                </div>
                {(pickerDiscipline !== 'all' || pickerSearch) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setPickerDiscipline('all'); setPickerSearch(''); }}
                    data-testid="home-rail-picker-reset"
                  >
                    Reset filters · show all {allPickableItems.length} items
                  </Button>
                )}
                {allPickableItems.length === 0 && (
                  <div className="text-xs">
                    Nothing has been uploaded yet. Add library videos, events, or coaches first — they'll show up here automatically.
                  </div>
                )}
              </div>
            )}
            {filteredPickable.map((x) => {
              const on = isItemInActive(x.kind, x.id);
              return (
                  <label
                    key={`${x.kind}-${x.id}`}
                    className="flex items-center gap-3 p-2.5 hover:bg-muted/40 cursor-pointer"
                    data-testid={`home-rail-picker-item-${x.kind}-${x.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleItemInRail(x.kind, x.id)}
                      className="w-4 h-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {x.title}
                        {x.isDraft && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 whitespace-nowrap" title="Not published — won't appear on the home page until you publish it">
                            Draft
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {KIND_LABEL[x.kind]} {x.sub && `· ${x.sub}`}
                      </div>
                    </div>
                  </label>
              );
            })}
          </div>
          <DialogFooter className="px-6 py-4">
            <Button
              onClick={async () => {
                if (dirty) await save();
                setPickerIdx(null);
              }}
              disabled={saving}
              data-testid="home-rail-picker-done"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {dirty ? 'Save & Close' : 'Done'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePageRailsTab;

// ---- Featured Free Video card ----------------------------------------------
// Admin picker for the single "free preview" video that plays in the Netflix
// hero without a subscription. Only one video is active at a time — picking
// a new one replaces the old selection. Lives in this file (and tab) because
// it's semantically part of the homepage layout.
const FeaturedFreeVideoCard = () => {
  const [candidates, setCandidates] = React.useState([]);
  const [current, setCurrent] = React.useState({ kind: null, id: null });
  const [draft, setDraft] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/admin/featured-free-video/candidates`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setCandidates(r.data?.candidates || []);
      const cur = r.data?.current || { kind: null, id: null };
      setCurrent(cur);
      setDraft(cur.kind && cur.id ? `${cur.kind}:${cur.id}` : '');
    } catch (e) {
      toast.error('Could not load featured free video list');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const grouped = React.useMemo(() => {
    const g = {};
    candidates.forEach((c) => {
      if (!g[c.group]) g[c.group] = [];
      g[c.group].push(c);
    });
    return g;
  }, [candidates]);

  const selectedLabel = React.useMemo(() => {
    if (!current.kind || !current.id) return null;
    const match = candidates.find((c) => c.kind === current.kind && c.id === current.id);
    return match ? `${match.group} — ${match.title}` : `${current.kind}:${current.id}`;
  }, [current, candidates]);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const [kind, id] = draft ? draft.split(':') : [null, null];
      await axios.put(
        `${API_URL}/api/admin/featured-free-video`,
        { kind: kind || null, id: id || null },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success(kind ? 'Featured free video updated' : 'Featured free video cleared');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save featured free video');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setDraft('');
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/featured-free-video`,
        { kind: null, id: null },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success('Featured free video cleared');
      await load();
    } catch (e) {
      toast.error('Could not clear featured free video');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="featured-free-video-card" className="border-amber-400/40">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              Featured Free Video (hero)
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Pin one video to play in the top banner for everyone — no subscription required. Only one at a time.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Currently</div>
            <div className="text-xs font-semibold" data-testid="featured-free-current">
              {loading ? '…' : (selectedLabel || 'None')}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={loading || saving}
            className="w-full min-w-0 h-10 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="featured-free-select"
          >
            <option value="">— None (hide free preview) —</option>
            {Object.entries(grouped).map(([group, items]) => (
              <optgroup key={group} label={group}>
                {items.map((c) => (
                  <option key={`${c.kind}:${c.id}`} value={`${c.kind}:${c.id}`}>
                    {c.title}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <div className="flex gap-2 justify-end">
            {(current.kind || current.id) && (
              <Button variant="outline" onClick={clear} disabled={saving} data-testid="featured-free-clear">
                <X className="w-4 h-4 mr-2" /> Clear
              </Button>
            )}
            <Button onClick={save} disabled={saving || loading} data-testid="featured-free-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
